import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import { Session } from './utils/session';
import Sidebar from './Sidebar';
import { iterateDatesInRange, toInputDate } from '../utils/dateOnly';
import { parseApiDateTimeMs, toIstDateString } from '../utils/istTime';
import {
  buildLoginTimeMap,
  capTimeMapToLogin,
  formatLoginStartTime,
  getEarliestLoginOnDateAfter7Am,
  hmsToMs,
  isOnOrAfter7AmIst,
} from '../utils/loginTime';
import {
  buildActiveCallMeetingTaskTimeMap,
  buildClosedCallMeetingTaskTimeMap,
  buildFirstTaskStartMap,
  buildTaskTimeMap,
  sumUserTaskMinutesForDisplay,
} from '../utils/taskTime';
import { computeRowProductivityMs } from '../utils/activityTime';
import { fetchUsersListCached } from '../utils/usersListCache';
import { downloadCsv, downloadExcel, stampFileDate } from '../utils/exportCsv';
import PageLoader, { LoaderButtonLabel } from './PageLoader';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${Session.getToken()}`,
});

const MONTHS_BACK = 12;

function buildMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < MONTHS_BACK; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
}

function getMonthRange(monthValue) {
  const [y, m] = monthValue.split('-').map(Number);
  const fromDate = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastDay = new Date(y, m, 0);
  let toDate = toInputDate(lastDay);
  const today = toInputDate(new Date());
  if (toDate > today) toDate = today;
  return { fromDate, toDate };
}

/** "8h 05m" style duration; hours are hidden when zero (e.g. "12m"). */
function formatHm(ms) {
  const totalMinutes = Math.max(0, Math.round(Number(ms) / 60000));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

function formatReportDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d)
    .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    .replace(/ /g, '-');
}

function formatDayName(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'long' });
}

/**
 * Live logout SP writes Notes like:
 * "Task started at … but not stopped. Logout at … IST. Auto stop applied."
 * Count ONLY auto-logout / auto-stop notes — null or any other text must not count.
 */
function isAutoLogoutNote(notes) {
  const text = String(notes ?? '').trim().toLowerCase();
  if (!text) return false;
  if (text.includes('auto stop applied')) return true;
  if (/\bauto[\s_-]*logout\b/.test(text)) return true;
  return false;
}

export default function MonthlyReport() {
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [hideDesktop, setHideDesktop] = useState(false);
  const [month, setMonth] = useState(monthOptions[0].value);
  const [selectedUserId, setSelectedUserId] = useState('');
  // Input buffers — applied only when Search is clicked
  const [monthInput, setMonthInput] = useState(monthOptions[0].value);
  const [userInput, setUserInput] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportRows, setReportRows] = useState([]);
  const [userLogs, setUserLogs] = useState([]);
  const [startStopRecords, setStartStopRecords] = useState([]);
  const [usersById, setUsersById] = useState(() => new Map());
  const [travellingUserIds, setTravellingUserIds] = useState(() => new Set());
  const [frozenNow, setFrozenNow] = useState(() => new Date());

  const { fromDate, toDate } = useMemo(() => getMonthRange(month), [month]);

  const toggleSidebar = () => {
    if (window.innerWidth <= 991) {
      setShowMobileSidebar((s) => !s);
    } else {
      setHideDesktop((s) => !s);
    }
  };

  useEffect(() => {
    const overlay = document.getElementById('sidebar-overlay');
    if (overlay) {
      overlay.style.display = showMobileSidebar ? 'block' : 'none';
    }
    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      if (showMobileSidebar) sidebar.classList.add('show-mobile'); else sidebar.classList.remove('show-mobile');
      if (hideDesktop) sidebar.classList.add('hide-desktop'); else sidebar.classList.remove('hide-desktop');
    }
    const content = document.getElementById('content');
    if (content) {
      if (hideDesktop) content.classList.add('full-width'); else content.classList.remove('full-width');
    }
  }, [showMobileSidebar, hideDesktop]);

  useEffect(() => {
    const controller = new AbortController();

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const activityUrl = `${API_BASE_URL}/api/UserActivityReport/weekly?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
        const userLogsUrl = `${API_BASE_URL}/api/Auth/userlogs?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;
        const startStopUrl = `${API_BASE_URL}/api/StartStop/Start_Stop_ALL`;

        const [activityRes, logsRes, startStopRes, usersData] = await Promise.all([
          fetch(activityUrl, { headers: authHeaders(), signal: controller.signal }),
          fetch(userLogsUrl, { headers: authHeaders(), signal: controller.signal }),
          fetch(startStopUrl, { headers: authHeaders(), signal: controller.signal }),
          // No abort signal: shared cache request must survive StrictMode re-mount
          fetchUsersListCached().catch(() => []),
        ]);

        if (activityRes.status === 401 || logsRes.status === 401) {
          throw new Error('Unauthorized (401)');
        }

        const activityData = activityRes.ok ? await activityRes.json() : [];
        const logsData = logsRes.ok ? await logsRes.json() : [];
        const startStopData = startStopRes.ok ? await startStopRes.json() : [];

        setReportRows(Array.isArray(activityData) ? activityData : []);
        setUserLogs(Array.isArray(logsData) ? logsData : []);
        setStartStopRecords(Array.isArray(startStopData) ? startStopData : []);

        const nameMap = new Map();
        const travellingIds = new Set();
        (Array.isArray(usersData) ? usersData : []).forEach((u) => {
          const id = Number(u.userId ?? u.UserId);
          if (id <= 0) return;
          const userName = u.userName ?? u.UserName ?? '';
          const first = u.firstName ?? u.FirstName ?? '';
          const last = u.lastName ?? u.LastName ?? '';
          nameMap.set(id, `${first} ${last}`.trim() || userName || `User ${id}`);
          if (u.travellingUser ?? u.TravellingUser) travellingIds.add(id);
        });
        setUsersById(nameMap);
        setTravellingUserIds(travellingIds);
        setFrozenNow(new Date());
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error(err);
        setError(err.message || 'Something went wrong');
        setReportRows([]);
        setUserLogs([]);
        setStartStopRecords([]);
      } finally {
        // Aborted request must not hide the loader of the newer request
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadData();
    return () => controller.abort();
  }, [fromDate, toDate, refreshKey]);

  const handleSearch = () => {
    setMonth(monthInput);
    setSelectedUserId(userInput);
    setRefreshKey((k) => k + 1);
  };

  const loginTimeMap = useMemo(
    () => buildLoginTimeMap(userLogs, frozenNow),
    [userLogs, frozenNow]
  );

  const taskTimeMap = useMemo(() => {
    const raw = buildTaskTimeMap(startStopRecords, frozenNow, userLogs);
    return capTimeMapToLogin(raw, loginTimeMap);
  }, [startStopRecords, frozenNow, userLogs, loginTimeMap]);

  const closedCallMeetingMap = useMemo(() => {
    const raw = buildClosedCallMeetingTaskTimeMap(startStopRecords, frozenNow);
    return capTimeMapToLogin(raw, loginTimeMap);
  }, [startStopRecords, frozenNow, loginTimeMap]);

  const activeCallMeetingMap = useMemo(() => {
    const raw = buildActiveCallMeetingTaskTimeMap(startStopRecords, frozenNow);
    return capTimeMapToLogin(raw, loginTimeMap);
  }, [startStopRecords, frozenNow, loginTimeMap]);

  const firstTaskStartMap = useMemo(
    () => buildFirstTaskStartMap(startStopRecords, travellingUserIds),
    [startStopRecords, travellingUserIds]
  );

  const dailyRowByKey = useMemo(() => {
    const map = new Map();
    (reportRows || []).forEach((r) => {
      const uid = Number(r.userId ?? r.UserId);
      const wd = toInputDate(r.workDate ?? r.WorkDate);
      if (!uid || !wd) return;
      if (wd < fromDate || wd > toDate) return;
      map.set(`${uid}|${wd}`, r);
    });
    return map;
  }, [reportRows, fromDate, toDate]);

  /**
   * Auto Logout count from StartStop.Notes (works with old EXE — no new EXE needed).
   * Null / empty / unrelated notes → not counted.
   */
  const autoLogoutCountMap = useMemo(() => {
    const map = new Map();

    (startStopRecords || []).forEach((item) => {
      const notes = item.notes ?? item.Notes;
      if (!isAutoLogoutNote(notes)) return;

      const uid = Number(item.userid ?? item.Userid ?? item.userId ?? item.UserId);
      const stopRaw = item.stopTime ?? item.StopTime;
      const startRaw = item.startTime ?? item.StartTime;
      const when = stopRaw || startRaw;
      if (!uid || !when) return;

      const ms = parseApiDateTimeMs(when);
      if (Number.isNaN(ms)) return;
      const dateStr = toIstDateString(ms);
      if (!dateStr || dateStr < fromDate || dateStr > toDate) return;

      const key = `${uid}|${dateStr}`;
      map.set(key, (map.get(key) || 0) + 1);
    });

    return map;
  }, [startStopRecords, fromDate, toDate]);

  /** Fallback names from report rows, userlogs and task records when Users_List misses an id. */
  const fallbackNamesById = useMemo(() => {
    const map = new Map();
    const add = (uidRaw, nameRaw) => {
      const uid = Number(uidRaw);
      const name = (nameRaw || '').trim();
      if (!uid || !name || map.has(uid)) return;
      map.set(uid, name);
    };
    (reportRows || []).forEach((r) => add(r.userId ?? r.UserId, r.userName ?? r.UserName));
    (userLogs || []).forEach((l) => add(l.userId ?? l.UserId, l.userName ?? l.UserName));
    (startStopRecords || []).forEach((s) =>
      add(s.userid ?? s.Userid ?? s.userId ?? s.UserId, s.userName ?? s.UserName)
    );
    return map;
  }, [reportRows, userLogs, startStopRecords]);

  const resolveUserName = useMemo(
    () => (uid) => usersById.get(uid) || fallbackNamesById.get(uid) || `User ${uid}`,
    [usersById, fallbackNamesById]
  );

  const userOptions = useMemo(() => {
    const ids = new Set();
    const collect = (key) => {
      const uid = Number(String(key).split('|')[0]);
      if (uid > 0) ids.add(uid);
    };
    loginTimeMap.forEach((_ms, key) => collect(key));
    taskTimeMap.forEach((ms, key) => { if (ms > 0) collect(key); });
    dailyRowByKey.forEach((_row, key) => collect(key));
    return [...ids]
      .map((id) => ({ id, name: resolveUserName(id) }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [loginTimeMap, taskTimeMap, dailyRowByKey, resolveUserName]);

  const employeeGroups = useMemo(() => {
    const monthDates = iterateDatesInRange(fromDate, toDate);
    const filterUid = selectedUserId ? Number(selectedUserId) : null;

    const datesByUser = new Map();
    const addKey = (key) => {
      const [uidStr, dateStr] = String(key).split('|');
      const uid = Number(uidStr);
      if (!uid || !dateStr) return;
      if (dateStr < fromDate || dateStr > toDate) return;
      if (filterUid && uid !== filterUid) return;
      if (!datesByUser.has(uid)) datesByUser.set(uid, new Set());
      datesByUser.get(uid).add(dateStr);
    };
    loginTimeMap.forEach((ms, key) => { if (ms > 0) addKey(key); });
    taskTimeMap.forEach((ms, key) => { if (ms > 0) addKey(key); });
    dailyRowByKey.forEach((_row, key) => addKey(key));
    autoLogoutCountMap.forEach((_count, key) => addKey(key));

    const meetingNeutralLock = new Map();
    const wasCallMeetingActive = new Map();
    const neutralMax = new Map();

    const getApiDayTimesMs = (uid, workDate) => {
      const dayRow = dailyRowByKey.get(`${uid}|${workDate}`);
      if (!dayRow) return null;

      const totalMs = hmsToMs(dayRow.totalTime ?? dayRow.TotalTime);
      const productiveMs = hmsToMs(dayRow.productive ?? dayRow.Productive);
      const neutralMs = hmsToMs(dayRow.neutral ?? dayRow.Neutral);
      const clickMs = hmsToMs(dayRow.clickProductive ?? dayRow.ClickProductive);

      if (totalMs <= 0 && productiveMs <= 0 && neutralMs <= 0 && clickMs <= 0) {
        return null;
      }

      const resolvedProductive = productiveMs > 0 ? productiveMs : clickMs;
      const resolvedNeutral = neutralMs > 0
        ? neutralMs
        : Math.max(0, totalMs - resolvedProductive);

      return {
        totalMs: totalMs > 0 ? totalMs : resolvedProductive + resolvedNeutral,
        productiveMs: resolvedProductive,
        neutralMs: resolvedNeutral,
      };
    };

    const buildDayRow = (uid, dateStr) => {
      const key = `${uid}|${dateStr}`;
      const isTravelling = travellingUserIds.has(uid);
      const taskMs = sumUserTaskMinutesForDisplay(
        startStopRecords,
        uid,
        dateStr,
        dateStr,
        frozenNow
      ) * 60000;

      let startTime = '—';
      if (isTravelling) {
        const raw = firstTaskStartMap.get(key);
        if (raw && isOnOrAfter7AmIst(raw, dateStr)) startTime = formatLoginStartTime(raw);
      } else {
        const earliestMs = getEarliestLoginOnDateAfter7Am(userLogs, uid, dateStr);
        if (earliestMs != null) startTime = formatLoginStartTime(earliestMs);
      }

      let totalMs;
      let productiveMs;
      let neutralMs;

      if (isTravelling) {
        totalMs = taskTimeMap.get(key) || 0;
        productiveMs = totalMs;
        neutralMs = 0;
      } else {
        const apiTimes = getApiDayTimesMs(uid, dateStr);
        if (apiTimes) {
          totalMs = apiTimes.totalMs;
          productiveMs = apiTimes.productiveMs;
          neutralMs = apiTimes.neutralMs;
        } else {
          totalMs = loginTimeMap.get(key) || 0;
          const dayRow = dailyRowByKey.get(key) || {};
          const times = computeRowProductivityMs({
            totalMs,
            row: dayRow,
            startStopRecords,
            userId: uid,
            workDate: dateStr,
            closedCallMeetingMap,
            activeCallMeetingMap,
            referenceNow: frozenNow,
            meetingNeutralLockRef: meetingNeutralLock,
            wasCallMeetingActiveRef: wasCallMeetingActive,
            neutralMaxRef: neutralMax,
          });
          productiveMs = times.productiveMs;
          neutralMs = times.neutralMs;
        }
      }

      return {
        date: dateStr,
        startTime,
        totalMs,
        taskMs,
        productiveMs,
        neutralMs,
        autoLogoutCount: autoLogoutCountMap.get(key) || 0,
      };
    };

    const groups = [];
    datesByUser.forEach((dateSet, uid) => {
      const dates = monthDates.filter((d) => dateSet.has(d));
      if (!dates.length) return;

      const days = dates.map((d) => buildDayRow(uid, d));
      const totals = days.reduce(
        (acc, day) => ({
          totalMs: acc.totalMs + day.totalMs,
          taskMs: acc.taskMs + day.taskMs,
          productiveMs: acc.productiveMs + day.productiveMs,
          neutralMs: acc.neutralMs + day.neutralMs,
          autoLogoutCount: acc.autoLogoutCount + day.autoLogoutCount,
        }),
        { totalMs: 0, taskMs: 0, productiveMs: 0, neutralMs: 0, autoLogoutCount: 0 }
      );

      groups.push({
        userId: uid,
        userName: resolveUserName(uid),
        days,
        totals,
      });
    });

    return groups.sort((a, b) =>
      a.userName.localeCompare(b.userName, undefined, { sensitivity: 'base' })
    );
  }, [
    fromDate,
    toDate,
    selectedUserId,
    loginTimeMap,
    taskTimeMap,
    dailyRowByKey,
    autoLogoutCountMap,
    travellingUserIds,
    startStopRecords,
    userLogs,
    firstTaskStartMap,
    closedCallMeetingMap,
    activeCallMeetingMap,
    frozenNow,
    resolveUserName,
  ]);

  const monthLabel = useMemo(
    () => monthOptions.find((m) => m.value === month)?.label ?? month,
    [monthOptions, month]
  );

  const MONTHLY_EXPORT_HEADERS = [
    'Employee',
    'Date',
    'Day',
    'Start Time',
    'Total Logged In Time',
    'Total Task Time',
    'Productive Time',
    'Neutral Time',
    'Auto Logout',
  ];

  const buildMonthlyExportRows = () => {
    const rows = [];
    employeeGroups.forEach((group) => {
      group.days.forEach((day) => {
        rows.push([
          group.userName,
          formatReportDate(day.date),
          formatDayName(day.date),
          day.startTime,
          formatHm(day.totalMs),
          formatHm(day.taskMs),
          formatHm(day.productiveMs),
          formatHm(day.neutralMs),
          day.autoLogoutCount > 0 ? day.autoLogoutCount : '',
        ]);
      });
      rows.push([
        `${group.userName} - Total`,
        '',
        '',
        '',
        formatHm(group.totals.totalMs),
        formatHm(group.totals.taskMs),
        formatHm(group.totals.productiveMs),
        formatHm(group.totals.neutralMs),
        group.totals.autoLogoutCount > 0 ? group.totals.autoLogoutCount : '',
      ]);
      rows.push([]);
    });
    return rows;
  };

  const handleExportCsv = () => {
    downloadCsv(
      `monthly-report-${month}-${stampFileDate()}.csv`,
      MONTHLY_EXPORT_HEADERS,
      buildMonthlyExportRows()
    );
  };

  const handleExportExcel = () => {
    downloadExcel(
      `monthly-report-${month}-${stampFileDate()}.xls`,
      MONTHLY_EXPORT_HEADERS,
      buildMonthlyExportRows()
    );
  };

  return (
    <div className="app-wrapper">
      <div
        id="sidebar-overlay"
        className="sidebar-overlay"
        onClick={() => setShowMobileSidebar(false)}
      />

      <Sidebar />

      <div id="content">
        <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 7, marginBottom: 32 }}>
          <div className="d-flex align-items-center">
            <button className="btn btn-toggle me-3" onClick={toggleSidebar}>
              <i className="fas fa-bars" />
            </button>
            <h2 className="mb-0">Monthly Report</h2>
          </div>
        </div>

        <div className="main-card">
          <div className="row g-3 mb-4 align-items-end">
            <div className="col-xl-3 col-md-4">
              <label className="small text-muted">Month</label>
              <select
                className="form-control"
                value={monthInput}
                onChange={(e) => setMonthInput(e.target.value)}
              >
                {monthOptions.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-xl-3 col-md-4">
              <label className="small text-muted">User</label>
              <select
                className="form-control"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
              >
                <option value="">All Users</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-xl-2 col-md-4">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSearch}
                disabled={loading}
              >
                <LoaderButtonLabel loading={loading} loadingLabel="Searching...">
                  Search
                </LoaderButtonLabel>
              </button>
            </div>
            <div className="col-xl-4 col-md-12 d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-success btn-sm"
                onClick={handleExportExcel}
                disabled={loading || employeeGroups.length === 0}
              >
                <i className="fas fa-file-excel me-1" aria-hidden="true" />
                Export Excel
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleExportCsv}
                disabled={loading || employeeGroups.length === 0}
              >
                <i className="fas fa-file-csv me-1" aria-hidden="true" />
                Export CSV
              </button>
            </div>
          </div>

          <p className="mb-3">
            <strong>Reporting Month:</strong> {monthLabel}
          </p>

          {loading ? (
            <PageLoader label="Loading monthly report..." />
          ) : error ? (
            <div className="p-3 text-center text-danger">Error: {error}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered monthly-report-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Start Time</th>
                    <th>Total Logged In Time</th>
                    <th>Total Task Time</th>
                    <th>Productive Time</th>
                    <th>Neutral Time</th>
                    <th>Auto Logout</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeGroups.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-3">
                        No records found for {monthLabel}.
                      </td>
                    </tr>
                  )}
                  {employeeGroups.map((group, groupIndex) => (
                    <React.Fragment key={group.userId}>
                      {group.days.map((day) => (
                        <tr key={`${group.userId}-${day.date}`}>
                          <td>{group.userName}</td>
                          <td>{formatReportDate(day.date)}</td>
                          <td>{formatDayName(day.date)}</td>
                          <td>{day.startTime}</td>
                          <td>{formatHm(day.totalMs)}</td>
                          <td>{formatHm(day.taskMs)}</td>
                          <td>{formatHm(day.productiveMs)}</td>
                          <td>{formatHm(day.neutralMs)}</td>
                          <td>{day.autoLogoutCount > 0 ? day.autoLogoutCount : ''}</td>
                        </tr>
                      ))}
                      <tr className="monthly-report-total-row">
                        <td>{group.userName} – Total</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td>{formatHm(group.totals.totalMs)}</td>
                        <td>{formatHm(group.totals.taskMs)}</td>
                        <td>{formatHm(group.totals.productiveMs)}</td>
                        <td>{formatHm(group.totals.neutralMs)}</td>
                        <td>{group.totals.autoLogoutCount > 0 ? group.totals.autoLogoutCount : ''}</td>
                      </tr>
                      {groupIndex < employeeGroups.length - 1 && (
                        <tr className="monthly-report-spacer-row">
                          <td colSpan={9}>&nbsp;</td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
